import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const RoleDetails = ({ role, onDeleteRole }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(role.name);
  const [editedDescription, setEditedDescription] = useState(role.description);

  const handleSaveEdit = () => {
    // In real app, this would update the role
    console.log('Updating role:', { name: editedName, description: editedDescription });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedName(role.name);
    setEditedDescription(role.description);
    setIsEditing(false);
  };

  const getRoleStatusColor = (userCount) => {
    if (userCount === 0) return 'text-secondary-400 bg-secondary-100';
    if (userCount < 10) return 'text-warning bg-warning-100';
    if (userCount < 50) return 'text-success bg-success-100';
    return 'text-primary bg-primary-100';
  };

  const getRoleTypeIcon = () => {
    if (role.name.toLowerCase().includes('admin')) return 'Crown';
    if (role.name.toLowerCase().includes('manager')) return 'Users';
    if (role.name.toLowerCase().includes('lead')) return 'UserCheck';
    if (role.name.toLowerCase().includes('guest')) return 'UserMinus';
    return 'User';
  };

  return (
    <div className="bg-surface rounded-lg border border-secondary-200 p-6 mb-6">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <Icon name={getRoleTypeIcon()} size={24} className="text-primary" />
          </div>
          
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="
                    w-full text-xl font-semibold border border-secondary-200 rounded-md px-3 py-2
                    focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  "
                  placeholder="Role name"
                />
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  rows={2}
                  className="
                    w-full border border-secondary-200 rounded-md px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                    resize-none
                  "
                  placeholder="Role description"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="
                      bg-success text-white px-3 py-1.5 rounded-md text-sm font-medium
                      hover:bg-success-600 transition-colors duration-150
                      flex items-center gap-1
                    "
                  >
                    <Icon name="Check" size={14} />
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="
                      border border-secondary-300 text-text-primary px-3 py-1.5 rounded-md text-sm font-medium
                      hover:bg-secondary-50 transition-colors duration-150
                      flex items-center gap-1
                    "
                  >
                    <Icon name="X" size={14} />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-text-primary mb-2">
                  {role.name}
                </h2>
                <p className="text-text-secondary mb-4">
                  {role.description}
                </p>
              </>
            )}
          </div>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="
                p-2 rounded-md hover:bg-secondary-50 transition-colors duration-150
                text-secondary-500 hover:text-primary
              "
              title="Edit role details"
            >
              <Icon name="Edit2" size={16} />
            </button>
            <button
              onClick={() => onDeleteRole(role)}
              className="
                p-2 rounded-md hover:bg-error-50 transition-colors duration-150
                text-secondary-500 hover:text-error
              "
              title="Delete role"
            >
              <Icon name="Trash2" size={16} />
            </button>
          </div>
        )}
      </div>

      {!isEditing && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* User Count */}
          <div className="bg-background rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Assigned Users</span>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleStatusColor(role.userCount)}`}>
                {role.userCount === 0 ? 'Unused' : 'Active'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-text-primary">
                {role.userCount}
              </span>
              <Icon name="Users" size={16} className="text-secondary-400" />
            </div>
          </div>

          {/* Role Level */}
          <div className="bg-background rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Hierarchy Level</span>
              <Icon name="TreePine" size={14} className="text-secondary-400" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-text-primary">
                {role.level}
              </span>
              <span className="text-sm text-text-secondary">
                {role.level === 0 ? 'Root' : 'Child'}
              </span>
            </div>
          </div>

          {/* Permission Count */}
          <div className="bg-background rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Active Permissions</span>
              <Icon name="Shield" size={14} className="text-secondary-400" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-text-primary">
                {Object.values(role.permissions).reduce((count, module) => 
                  count + Object.values(module).filter(Boolean).length, 0
                )}
              </span>
              <span className="text-sm text-text-secondary">
                of {Object.values(role.permissions).reduce((count, module) => 
                  count + Object.keys(module).length, 0
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Inheritance Indicator */}
      {role.parentId && !isEditing && (
        <div className="mt-4 p-3 bg-accent-50 border border-accent-200 rounded-md">
          <div className="flex items-center gap-2">
            <Icon name="ArrowDown" size={16} className="text-accent" />
            <span className="text-sm text-accent font-medium">
              Inherits permissions from parent role
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleDetails;